import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { createPortal } from "react-dom";
import { contrastText } from "./color";
import { fullName, sortByName } from "./players";
const ROW_BG = "rgba(15, 23, 42, 0.04)";
const ROW_BG_HOVER = "rgba(15, 23, 42, 0.07)";
const TEXT_PRIMARY = "#0f172a";
const TEXT_SECONDARY = "rgba(15, 23, 42, 0.65)";
// Custom bottom sheet portaled to document.body — no MUI Drawer/Modal/Dialog.
// Visually mirrors the desktop Bench rows so users see the same affordances on
// both surfaces.
export const FormationPickerDrawer = ({ open, slot, currentPlayer, benchPlayers, primaryColour, onClose, onSelectBenchPlayer, onRemoveCurrent, }) => {
    const sorted = useMemo(() => sortByName(benchPlayers), [benchPlayers]);
    const numberTextColour = contrastText(primaryColour);
    if (!open)
        return null;
    return createPortal(_jsxs(Box, { "data-testid": "formation-picker-drawer", children: [_jsx(Box, { onClick: onClose, sx: {
                    position: "fixed",
                    inset: 0,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    zIndex: 1500,
                } }), _jsxs(Box, { sx: {
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "70vh",
                    backgroundColor: "#fff",
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    zIndex: 1501,
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 -4px 12px rgba(0,0,0,0.15)",
                }, children: [_jsxs(Box, { sx: {
                            flexShrink: 0,
                            pt: 1,
                            pb: 1.25,
                            px: 2,
                            borderBottom: "1px solid rgba(15,23,42,0.06)",
                        }, children: [_jsx(Box, { sx: {
                                    width: 36,
                                    height: 4,
                                    backgroundColor: "rgba(15,23,42,0.2)",
                                    borderRadius: 2,
                                    mx: "auto",
                                    mb: 1,
                                } }), _jsxs(Stack, { direction: "row", alignItems: "baseline", spacing: 1, sx: { mb: 0.25 }, children: [_jsx(Typography, { variant: "overline", color: "text.secondary", sx: { lineHeight: 1 }, children: "Position" }), _jsx(Typography, { variant: "h6", component: "h2", sx: { fontWeight: 700, lineHeight: 1, color: TEXT_PRIMARY }, children: slot?.label ?? "" })] }), _jsx(Typography, { variant: "body2", sx: { color: TEXT_SECONDARY }, children: currentPlayer
                                    ? `Currently: #${currentPlayer.squadNumber ?? "–"} ${fullName(currentPlayer)}`
                                    : "Choose a player to assign." })] }), _jsx(Box, { sx: {
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            WebkitOverflowScrolling: "touch",
                            px: 1.5,
                            py: 1.5,
                        }, children: _jsxs(Stack, { spacing: 0.75, children: [currentPlayer && (_jsxs(Box, { role: "button", "data-testid": "drawer-remove", onClick: onRemoveCurrent, sx: {
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        px: 1.5,
                                        py: 1.5,
                                        minHeight: 56,
                                        borderRadius: 1,
                                        backgroundColor: "rgba(220, 38, 38, 0.06)",
                                        border: "1px solid rgba(220, 38, 38, 0.20)",
                                        color: "rgb(185, 28, 28)",
                                        cursor: "pointer",
                                        userSelect: "none",
                                        "&:hover": { backgroundColor: "rgba(220, 38, 38, 0.10)" },
                                        "&:active": { backgroundColor: "rgba(220, 38, 38, 0.10)" },
                                    }, children: [_jsx(DeleteOutlineIcon, {}), _jsxs(Typography, { sx: { fontWeight: 600, fontSize: "1rem" }, children: ["Remove ", fullName(currentPlayer), " from pitch"] })] })), sorted.length === 0 && !currentPlayer && (_jsx(Typography, { sx: {
                                        color: TEXT_SECONDARY,
                                        fontStyle: "italic",
                                        fontSize: "0.85rem",
                                        textAlign: "center",
                                        py: 2,
                                    }, children: "No players available" })), sorted.map((player) => (_jsxs(Box, { role: "button", "data-testid": `drawer-player-${player.id}`, onClick: () => onSelectBenchPlayer(player), sx: {
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.25,
                                        pl: 0,
                                        pr: 1.5,
                                        py: 1.25,
                                        minHeight: 56,
                                        borderRadius: 1,
                                        backgroundColor: ROW_BG,
                                        borderLeft: `4px solid ${primaryColour}`,
                                        color: TEXT_PRIMARY,
                                        cursor: "pointer",
                                        userSelect: "none",
                                        "&:hover": { backgroundColor: ROW_BG_HOVER },
                                        "&:active": { backgroundColor: ROW_BG_HOVER },
                                    }, children: [_jsx(Box, { sx: {
                                                ml: 1.25,
                                                width: 36,
                                                height: 36,
                                                borderRadius: "50%",
                                                backgroundColor: primaryColour,
                                                border: "1.5px solid rgba(15, 23, 42, 0.55)",
                                                color: numberTextColour,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontWeight: 800,
                                                fontSize: "0.95rem",
                                                flexShrink: 0,
                                            }, children: player.squadNumber ?? "-" }), _jsx(Typography, { noWrap: true, sx: {
                                                flex: 1,
                                                fontWeight: 600,
                                                fontSize: "1rem",
                                            }, children: fullName(player) }), player.primaryPosition && (_jsx(Box, { sx: {
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: 0.75,
                                                backgroundColor: "rgba(15, 23, 42, 0.06)",
                                                border: "1px solid rgba(15, 23, 42, 0.12)",
                                                color: TEXT_SECONDARY,
                                                fontSize: "0.75rem",
                                                fontWeight: 800,
                                                letterSpacing: 0.5,
                                                lineHeight: 1,
                                                flexShrink: 0,
                                            }, children: player.primaryPosition }))] }, player.id)))] }) })] })] }), document.body);
};
